import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Layers, FileText, Truck, ClipboardCheck, AlertTriangle, Package } from "lucide-react";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle } from "@/components/ops-ui";

const Stat = ({ label, value, icon: Icon, tone = "slate", testid, compact }) => (
  <Card data-testid={testid} className="border-slate-200">
    <CardContent className={compact ? "p-3" : "p-5"}>
      <div className="flex items-start justify-between">
        <div>
          <div className="caption">{label}</div>
          <div className={`font-heading font-bold ${compact ? "text-2xl mt-1" : "text-3xl mt-2"}`}>{value ?? "—"}</div>
        </div>
        <div className={`${compact ? "w-8 h-8" : "w-10 h-10"} rounded-md grid place-items-center bg-${tone}-100`}>
          <Icon className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-${tone}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const { density, setDensity } = useUiPrefs("dashboard");
  const compact = density === "compact";

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data)).catch(()=>{});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="caption">Operations overview</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-1 tracking-tight">
            Hey {user?.name?.split(" ")[0]}, here&apos;s your inventory pulse.
          </h1>
          <p className="text-slate-500 mt-2 max-w-xl">
            {user?.role === "admin"
              ? "Full financial visibility — costs, freight, and landed averages."
              : "Warehouse view — quantities, CBM, and pending arrivals."}
          </p>
        </div>
        <DensityToggle density={density} setDensity={setDensity} />
      </div>

      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 ${compact ? "gap-3" : "gap-4"}`}>
        <Stat testid="stat-skus" label="Master SKUs" value={stats.total_skus} icon={Layers} tone="sky" compact={compact} />
        <Stat testid="stat-invoices" label="Invoices / POs" value={stats.total_invoices} icon={FileText} tone="indigo" compact={compact} />
        <Stat testid="stat-shipments" label="Shipments" value={stats.total_shipments} icon={Truck} tone="violet" compact={compact} />
        <Stat testid="stat-pending" label="Pending Arrivals" value={stats.pending_arrivals} icon={ClipboardCheck} tone="amber" compact={compact} />
        <Stat testid="stat-shortages" label="Shortage Alerts" value={stats.shortages} icon={AlertTriangle} tone="rose" compact={compact} />
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="caption">Quick guide</div>
          <h3 className="font-heading text-xl font-bold mt-1">The 4-step workflow</h3>
          <ol className="mt-4 space-y-3 text-sm text-slate-600">
            <li><span className="font-mono text-slate-400 mr-2">01</span><b>Add SKUs</b> — build the master sheet with rack codes.</li>
            <li><span className="font-mono text-slate-400 mr-2">02</span><b>Record invoices</b> — qty + CNY now, RM & invoice-no can be edited later.</li>
            <li><span className="font-mono text-slate-400 mr-2">03</span><b>Group into shipments</b> — assign items under one warehouse tracking code with pre-CBM + ETA.</li>
            <li><span className="font-mono text-slate-400 mr-2">04</span><b>Arrival check-in</b> — verify qty & actual CBM. Photos on shortages.</li>
          </ol>
        </div>
        <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 relative overflow-hidden">
          <Package className="absolute -right-4 -bottom-4 w-40 h-40 text-white/5" />
          <div className="caption text-sky-300">Landed cost</div>
          <h3 className="font-heading text-xl font-bold mt-1">Annual average per piece</h3>
          <p className="text-slate-300 text-sm mt-3">
            Freight fee is allocated to each SKU proportional to its pre-CBM inside the shipment, giving you a true CNY & RM cost per pc across every year.
          </p>
          {user?.role === "admin" && (
            <a href="/cost-analysis" className="mt-4 inline-flex text-sky-300 hover:text-sky-200 text-sm font-medium">
              Open Cost Analysis →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
